import assignGlobal from 'js/utils/assign_global';

import { MentionsSettingsContainer } from 'js/settings/components/mentions_settings/mentions_settings_container';
import renderOnsitePing from 'js/onsite_pings/onsite_pings.wjs';
import 'js/i18n/translations/affiliation_editor';
import { ProfileFeed } from 'js/social/ProfileFeed';

import registerForRails from 'js/utils/registerForRails';
import { trackClick } from 'js/utils/trackClick';
import { currentUser } from 'js/utils/user';
import { watchImpressions } from 'js/utils/impressions/watchImpressions';
import { ProfileCheckPaperUpdate } from 'js/social/ProfileCheckPaperUpdate';
import { Pill } from 'js/social/shared/design_system/Pill';
import SuggestedAcademics from 'js/social/SuggestedAcademics';
import { setUpLeftSidebarButtons } from 'js/profiles/setUpLeftSidebarButtons';
import { setUpAutotestSignupPrompts } from 'js/profiles/setUpAutotestSignupPrompts';
import {
  getMentionsCount,
  getCitationsDashboardCounts,
} from 'js/profiles/api/mentions_api';
import formatNumber from 'js/utils/format';
import { setUpFigureCarousel } from 'js/single_work/loswp/setUpFigureCarousel';
import { ProfileList } from 'js/podcast_ai/ProfileList';
import { SimplePaperPodcast } from 'js/podcast_ai/SimplePaperPodcast';
import { PodcastLibraryProfileBanner } from 'js/podcast_library/PodcastLibraryProfileBanner';
import RelatedAuthorProfileImage from 'js/social/profile/RelatedAuthorProfileImage';
import ImpactHubModal from 'js/modals/ImpactHubModal';
import { ProfileCiteeClaimModal } from 'js/social/ProfileCiteeClaimModal';

assignGlobal('Aedu', { renderOnsitePing: renderOnsitePing });
assignGlobal('Aedu', { MentionsSettingsContainer: MentionsSettingsContainer });

registerForRails({
  ProfileFeed,

  ProfileList,
  ProfileCheckPaperUpdate,
  Pill,
  SuggestedAcademics,
  SimplePaperPodcast,
  PodcastLibraryProfileBanner,
  RelatedAuthorProfileImage,
  ImpactHubModal,
  ProfileCiteeClaimModal,
});

// click tracking on profile page
// wrapped in if statement for profile_page_related_authors AB test
// clicks are not tracked when vieweing own profile page because this
// test implements no changes on when a user is logged in and
// viewing their own profile page, making self prof pg clicks
// irrelevant
if (currentUser == null || currentUser.id !== window.$viewedUser.id) {
  // Claims in profile-specific tracking
  document.querySelectorAll('.js-profile-claim').forEach((el) => {
    el.addEventListener('click', () => {
      const workStrip = el.closest('.js-work-strip');
      const workId = workStrip?.getAttribute('data-work-id');

      trackClick({
        source: 'profile-claim',
        entityId: workId ? parseInt(workId, 10) : null,
        extra: {
          visited_user_id: window.$viewedUser.id,
        },
      });
    });
  });

  document.querySelectorAll('[data-click-track]').forEach((el) => {
    // Skip claim links as they're handled above. Need to remove after claims in profile test
    if (el.classList.contains('js-profile-claim')) return;

    el.addEventListener('click', () => {
      trackClick({ source: el.getAttribute('data-click-track') });
    });
  });
}

document.querySelectorAll('[data-impressions').forEach((el) => {
  const source = 'soc_prof_info_sugg';
  watchImpressions(el, { source: source });
});

// This used to be done in the controller, but the CombinedMention query
// takes far too long.

// A profile sub-page URL (e.g. /:username/mentions), preserving query string.
const profileSubPath = (segment) => {
  const url = new URL(window.location.href);
  const pathname = url.pathname.endsWith('/')
    ? url.pathname.slice(0, -1)
    : url.pathname;
  return `${pathname}/${segment}${url.search}`;
};

// Fill a stat-row placeholder with a linked count. Hidden for logged-out
// visitors below 10 (matching the legacy Mentions row threshold).
const fillStatRow = (containerEl, { label, count, href, trackingSource }) => {
  if (!containerEl) return;
  if (!(count > 10 || (currentUser && count > 0))) return;

  containerEl.innerHTML = `
    <a href="${href}">
      <div class="stat-container">
        <p class="label">${label}</p>
        <p class="data">${formatNumber(count)}</p>
      </div>
    </a>
  `;

  const linkEl = containerEl.querySelector('a');
  linkEl.addEventListener('click', () => {
    trackClick({ source: trackingSource });
  });
};

// Legacy path: publicly-confirmed mentions count only.
const loadMentionsCount = async () => {
  const containerEl = document.querySelector('.js-mentions-count-container');
  if (!containerEl) return;

  const mentionsCount = await getMentionsCount(window.$viewedUser.id);
  fillStatRow(containerEl, {
    label: 'Mentions',
    count: mentionsCount,
    href: profileSubPath('mentions'),
    trackingSource: 'profile-user-info-mentions',
  });
};

// Citations-dashboard path: Citations + Mentions counts from the dashboard's
// category logic, so the profile rows match the dashboard and their list pages.
const loadCitationsDashboardCounts = async () => {
  const { citations, mentions } = await getCitationsDashboardCounts(
    window.$viewedUser.id,
  );

  fillStatRow(document.querySelector('.js-citations-count-container'), {
    label: 'Citations',
    count: citations,
    href: profileSubPath('citations'),
    trackingSource: 'profile-user-info-citations',
  });
  fillStatRow(document.querySelector('.js-mentions-count-container'), {
    label: 'Mentions',
    count: mentions,
    href: profileSubPath('mentions'),
    trackingSource: 'profile-user-info-mentions',
  });
};

// The Citations placeholder is server-rendered only for viewers in a citations
// dashboard variant; its presence switches both rows to dashboard-sourced counts.
const loadStatCounts = () => {
  if (document.querySelector('.js-citations-count-container')) {
    loadCitationsDashboardCounts();
  } else {
    loadMentionsCount();
  }
};

// Call immediately if loaded, otherwise call on DOMContentLoaded
if (document.readyState === 'complete') {
  loadStatCounts();
} else {
  document.addEventListener('DOMContentLoaded', loadStatCounts);
}

setUpFigureCarousel();
setUpLeftSidebarButtons();
setUpAutotestSignupPrompts();
